"use client";

import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { Button } from "@components/core/Button";
import Stop20 from "@ui/icons/20x20/stop-20.svg";
import { cn } from "@ui/lib/utils";
import { AnimatePresence } from "motion/react";
import * as m from "motion/react-m";
import { useTranslations } from "next-intl";
import { useContext, useState } from "react";

import { STSContext } from "../STS";
import { formatDuration } from "../utils";

export default function Recording() {
  const [promptIndex, setPromptIndex] = useState(0);
  const { audioRecorderRef, duration, stsPrompts } = useContext(STSContext);
  const t = useTranslations("ClientKeys.Globals");

  function stopRecording() {
    captureEvent(EventName.mw_sts_recording_stopped, {
      duration_seconds: duration ?? 0,
      prompt_index: promptIndex,
    });
    audioRecorderRef.current?.stopRecording();
  }

  return (
    <>
      <div className='flex flex-1 pt-72'>
        <div className='flex flex-1 flex-col items-center justify-center rounded-lg'>
          <AnimatePresence mode='wait'>
            <m.div
              animate={{ opacity: 1, filter: "blur(0px)", y: 0, scale: 1 }}
              className='will-change-transform'
              exit={{ opacity: 0, filter: "blur(6px)", y: -6, scale: 0.98 }}
              initial={{ opacity: 0, filter: "blur(6px)", y: 4, scale: 0.98 }}
              key={`prompt-${promptIndex}`}
              transition={{
                ease: "easeInOut",
                duration: 0.5,
              }}
            >
              <p className='f-ui-03 mb-8 max-w-[300px] text-center text-light'>{stsPrompts[promptIndex].direction}</p>
              <p className='f-description-01 mb-12 max-w-[300px] text-center'>{stsPrompts[promptIndex].text}</p>
            </m.div>
          </AnimatePresence>
        </div>
      </div>
      <m.div
        animate={{ opacity: 1 }}
        className='relative flex items-center justify-between gap-8 p-16'
        initial={{ opacity: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className='flex items-center gap-16'>
          <Button className='w-40 p-0' onClick={stopRecording}>
            <Stop20 />
          </Button>
          <span className='f-ui-05 ms-auto tabular-nums' id='elapsed-time'>
            {formatDuration(duration as number)}
          </span>
        </div>
        <div className='absolute left-1/2 flex translate-x-[-50%] justify-center gap-8'>
          {stsPrompts.map((_, i) => {
            const active = promptIndex === i;
            return (
              <div
                className={cn(
                  "transition-color f-ui-04 relative flex h-32 w-32 items-center justify-center duration-300",
                  active ? "text-inverse" : "text-default",
                )}
                key={`prompt-${i}`}
              >
                {active && (
                  <m.div className='absolute -z-10 h-full w-full rounded-full bg-inverse' layoutId='prompt-indicator' />
                )}
                {i + 1}
              </div>
            );
          })}
        </div>
        <Button
          onClick={() => {
            captureEvent(EventName.mw_sts_prompt_completed, {
              prompt_index: promptIndex,
            });
            if (promptIndex === stsPrompts.length - 1) {
              return stopRecording();
            }
            setPromptIndex(promptIndex + 1);
          }}
        >
          {t("next")}
        </Button>
      </m.div>
    </>
  );
}
